<template>
  <div class="global-nav-menu">
    <div class="top-container">
      <SubscriptionInfoButton
        v-if="showSubscriptionButton"
        @click="$emit('open-subscription-modal')"
      />
      <Search
        v-if="modulesStore.isContractRoomModuleActive"
        class="global-nav-menu-search"
        :fixed-expanded="true"
        :collapse-enabled="true"
        @close="$emit('close')"
      />
      <div @click="$emit('close')">
        <router-link
          v-for="item in sidebarItemsStore.sidebarItems"
          :key="item.pathName"
          v-slot="{ href, navigate, isActive }"
          :to="{ name: item.pathName }"
          custom
        >
          <a
            v-wave
            :href="href"
            :class="{ 'is-active': isActive }"
            class="global-nav-menu-item"
            :data-external="`global-sidebar-:${item.label}-button`"
            @click="navigate"
          >
            <GlobalSidebarIconActive v-if="isActive" :type="item.label" />
            <GlobalSidebarIcon v-else :type="item.label" />
            <FriedParagraph class="text" :size="ParagraphSize.Small">
              {{ $t(item.label) }}
            </FriedParagraph>
            <FriedStatusBadge
              v-if="item.beta"
              class="beta-badge"
              text="Beta"
              :status-type="StatusType.Branded"
              :enable-min-width="false"
            />
          </a>
        </router-link>
      </div>
    </div>
    <div v-if="!isInactiveSubscription" class="actions-container">
      <CreateDocumentButton
        v-if="isXSmallScreenAndDown"
        @created="$emit('document-created', $event)"
        @create-document="$emit('create-document', $event)"
        @create-dsr="$emit('create-dsr')"
        @close="$emit('close')"
      />
      <ProductSwitcher
        v-if="showProductSwitcher"
        @set-module-type="$emit('set-module-type', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { ParagraphSize, StatusType } from '@getaccept/fried-tofu';
import { storeToRefs } from 'pinia';
import type { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import type { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import Search from '../search/components/Search.vue';
import GlobalSidebarIconActive from '../../sidebar/components/GlobalSidebarIconActive.vue';
import GlobalSidebarIcon from '../../sidebar/components/GlobalSidebarIcon.vue';
import ProductSwitcher from '../../sidebar/components/ProductSwitcher.vue';
import { useSidebarItemsStore } from '../../sidebar/store/sidebar-items.store';
import { useModulesStore } from '../../modules/store/modules.store';
import SubscriptionInfoButton from '../../subscription/components/SubscriptionInfoButton.vue';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';
import type { CreateDocumentSection } from '../../document-create/types/create-document-section';
import CreateDocumentButton from './CreateDocumentButton.vue';

defineEmits<{
  close: [];
  'document-created': [{ id: string; type: EnumDocumentType }];
  'create-dsr': [];
  'create-document': [CreateDocumentSection?];
  'set-module-type': [ModuleName];
  'open-subscription-modal': [];
}>();
const { isXSmallScreenAndDown } = useBreakpoints();

const sidebarItemsStore = useSidebarItemsStore();
const modulesStore = useModulesStore();

const { isInactiveSubscription, isTrialPlan } = storeToRefs(useSubscriptionStore());

const showProductSwitcher = computed(
  () => modulesStore.hasDealRoomModuleAccess && modulesStore.hasContractRoomModuleAccess
);

const showSubscriptionButton = computed(() => isTrialPlan.value && !isInactiveSubscription.value);
</script>

<style lang="scss" scoped>
.global-nav-menu {
  width: 100%;
  position: absolute;
  bottom: 0;
  height: calc(100% - var(--topbar-height));
  background: var(--white);
  padding: var(--spacing-100);
  z-index: 100000;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .global-nav-menu-search {
    margin: 0 0 var(--spacing-100);
  }

  .global-nav-menu-item {
    display: flex;
    align-items: center;
    padding: var(--spacing-75) var(--spacing-100);
    border: 0;
    background: transparent;
    border-radius: 6px;
    text-decoration: none;
    color: var(--text-dark);

    .text {
      margin-left: var(--spacing-100);
    }

    &.is-active {
      background: var(--gray-94);
    }
  }

  .actions-container {
    display: flex;
    flex-direction: column-reverse;
    gap: var(--spacing-100);
  }

  .beta-badge {
    margin-left: var(--spacing-100);
  }
}
</style>
